/**
 * Hosted Sun Dreamteam chrome (header/footer/styles/fonts/scripts).
 *
 * The files live under `/components/` on the Dreamteam hosts and must not be
 * modified. PRE serves `-pre` suffixed variants; PROD serves the bare names.
 */

const DEFAULT_BASE_URL = "https://pre-gaming.uat-dreamteamfc.com/components"

const BASE = (
  process.env.NEXT_PUBLIC_DT_COMPONENTS_BASE_URL || DEFAULT_BASE_URL
).replace(/\/+$/, "")

const SUFFIX = process.env.NEXT_PUBLIC_DT_COMPONENTS_VARIANT === "prod" ? "" : "-pre"

export const chromeUrls = {
  origin: new URL(BASE).origin,
  fonts: `${BASE}/fonts.css`,
  styles: `${BASE}/styles.css`,
  scripts: `${BASE}/scripts${SUFFIX}.js`,
} as const

export type ChromePart = "header" | "footer"

/**
 * Fetches a hosted chrome fragment and removes its `<script>` and `<link>`
 * tags. Scripts injected through `innerHTML` never execute, so the layout loads
 * `scripts.js` and the stylesheets itself; leaving the tags in would only add
 * dead markup. Returns an empty string on failure so a CDN blip degrades to a
 * chrome-less page instead of a 500.
 */
export async function fetchChrome(part: ChromePart): Promise<string> {
  const url = `${BASE}/${part}${SUFFIX}.html`

  try {
    const res = await fetch(url, { next: { revalidate: 300 } })

    if (!res.ok) {
      console.error(`[site-chrome] ${url} responded ${res.status}`)
      return ""
    }

    return (await res.text())
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<link\b[^>]*>/gi, "")
  } catch (error) {
    console.error(`[site-chrome] failed to fetch ${url}`, error)
    return ""
  }
}
